// NAPTÁR ADAT-LEKÉPEZÉS — TISZTA (render-mentes) függvények, hogy unit-tesztelhetők legyenek.
//
// A busz UTC ISO-ban tárol; a naptár `Europe/Budapest` zónában jelenít meg (a legacy `app.display_timezone`,
// egyezik a lista-formázókkal).
//
// ⚠️ MIÉRT NEM ELÉG A `timeZone: 'Europe/Budapest'` OPCIÓ (mért lelet): a FullCalendar NEVESÍTETT
// időzónához PLUGINT igényel (`@fullcalendar/luxon3` / moment-timezone). Plugin nélkül a nevesített zóna
// CSENDBEN UTC-ként viselkedik — a naptár tehát a UTC fal-időt mutatta (07:30), miközben a lista-oldalak és a
// szerkesztő-form Budapestet (09:30). Két óra eltérés ugyanarra a meetingre, hibajelzés nélkül.
//
// A MEGOLDÁS PLUGIN (és második dátum-lib) NÉLKÜL: a megjelenítési zónába SHIFTELVE adjuk át az időpontokat
// (a Budapest fal-idő „naiv" ISO-stringként), a naptárnak pedig `timeZone: 'UTC'`-t mondunk — így a rács
// pontosan a budapesti fal-időt rajzolja. A visszaút (drag & drop, slot-kattintás) ugyanezen a két tiszta
// függvényen megy vissza igazi UTC-be. A zóna-matematika a MÁR MEGLÉVŐ `date-fns-tz`-vel (nincs új dep).
// A konverzió EGY helyen van, és tesztelt — beleértve a DST-váltást (ott bukik minden naiv megoldás).
import { type MeetingRowSchema } from '@atlas/contracts';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { type z } from 'zod';

type MeetingRow = z.infer<typeof MeetingRowSchema>;

/** A megjelenítési zóna — a lista-formázókkal (meetings-format.ts) EGYEZŐEN Europe/Budapest. */
export const CALENDAR_TIMEZONE = 'Europe/Budapest';

/** Egy helyszín szín-hármasa (esemény-sáv háttér/szegély + a rá kerülő tinta) egy témában. */
export interface EventColors {
  bg: string;
  border: string;
  text: string;
}

/**
 * Helyszín-kulcs → esemény-színek. A `MeetingLocationBadge` szemantikus palettájával összhangban, de a
 * naptáron VIZUÁLISAN ELKÜLÖNÜLŐ hue-kra hangolva (két közeli zöld helyett tiszta hue-k), light+dark
 * variánssal. Ismeretlen helyszín → `other` (semleges).
 *
 * MIÉRT NYERS HEX ITT: ez ADAT-TÁBLA (nem komponens-stílus) — hét helyszín × két téma olyan palettát ad, ami
 * a token-készletben nem létezik (a tokenek szemantikusak: brand/success/danger…, nem „hét elkülönülő hue").
 * A színek EGY helyen élnek, és a komponens innen kéri őket; a design-system ESLint-fal a komponens-oldali
 * hardcode-ot tiltja, nem az adat-táblát.
 */
export const MEETING_EVENT_COLORS: Record<string, { light: EventColors; dark: EventColors }> = {
  meeting_room: {
    // Márka-zöld (accent) — a leggyakoribb helyszín az elsődleges hangsúlyt kapja.
    light: { bg: '#dcf3ea', border: '#229a5a', text: '#0c4a42' },
    dark: { bg: '#14332a', border: '#86d3b2', text: '#dcf3ea' },
  },
  breakout_room: {
    light: { bg: '#ffe4e6', border: '#e11d48', text: '#881337' },
    dark: { bg: '#3f1220', border: '#fda4af', text: '#ffe4e6' },
  },
  silent_box_1: {
    light: { bg: '#ede9fe', border: '#7c3aed', text: '#4c1d95' },
    dark: { bg: '#2e2250', border: '#c4b5fd', text: '#ede9fe' },
  },
  silent_box_2: {
    light: { bg: '#e0e7ff', border: '#4f46e5', text: '#312e81' },
    dark: { bg: '#262a5e', border: '#a5b4fc', text: '#e0e7ff' },
  },
  dining_room: {
    light: { bg: '#fef3c7', border: '#d97706', text: '#78350f' },
    dark: { bg: '#3a2c0a', border: '#fcd34d', text: '#fef3c7' },
  },
  keszthely_office: {
    light: { bg: '#e0f2fe', border: '#0284c7', text: '#0c4a6e' },
    dark: { bg: '#0c3149', border: '#7dd3fc', text: '#e0f2fe' },
  },
  other: {
    light: { bg: '#e2e8f0', border: '#475569', text: '#1e293b' },
    dark: { bg: '#2b3444', border: '#cbd5e1', text: '#e2e8f0' },
  },
};

/** A helyszín-kulcs szín-kulcsra fordítása (a fehérlistán kívüli/hiányzó → `other`). */
export function locationColorKey(locationKey: string | null): string {
  return locationKey && locationKey in MEETING_EVENT_COLORS ? locationKey : 'other';
}

/**
 * UTC ISO → a NAPTÁRNAK átadott „naiv" ISO (a megjelenítési zóna fal-ideje, offset NÉLKÜL).
 * Példa: `2026-08-31T07:30:00.000Z` → `2026-08-31T09:30:00` (Budapest, nyári időszámítás).
 * A naptár `timeZone: 'UTC'`-vel fut, tehát pontosan ezt a fal-időt rajzolja ki.
 */
export function utcIsoToCalendarInput(iso: string): string {
  return formatInTimeZone(new Date(iso), CALENDAR_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ss");
}

/**
 * A naptár „MOST"-ja — a megjelenítési zóna fal-ideje, ugyanabban a „naiv" alakban, mint az események. A naptár
 * `timeZone: 'UTC'`-vel fut, tehát a saját (valódi UTC) „most"-ja a „most" vonalat és a mai nap kiemelését UTC
 * szerint rajzolta: télen 1, nyáron 2 órával késve (mért hiba). Ezért a naptár `now` opcióját ez adja.
 */
export function calendarNow(at: Date = new Date()): string {
  return utcIsoToCalendarInput(at.toISOString());
}

/**
 * A naptárból VISSZAKAPOTT `Date` → igazi UTC ISO. A naptár `timeZone: 'UTC'`, tehát a `Date` UTC-mezői a
 * megjelenítési zóna fal-idejét hordozzák („fake-UTC") — ezt kell valódi instantra fordítani.
 * A DST-átmenet miatt a fal-időből NEM lehet fix offsettel számolni: a `fromZonedTime` a zóna-szabályokat
 * használja (ezért nem `getTime() - 2h`).
 */
export function calendarDateToUtcIso(date: Date): string {
  // A `Date` UTC-MEZŐIBŐL építjük a fal-idő stringet (a lokális mezők a böngésző zónáját hordoznák, ami
  // Budapesten kívül más eredményt adna).
  const pad = (n: number): string => String(n).padStart(2, '0');
  const naive =
    `${String(date.getUTCFullYear())}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
  return fromZonedTime(naive, CALENDAR_TIMEZONE).toISOString();
}

/** Egy naptár-esemény (a FullCalendar `EventInput` szűkített, saját alakja — így tesztelhető marad). */
export interface MeetingCalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  /**
   * Vonszolható/átméretezhető-e EZ az esemény. A FullCalendar esemény-szintű `editable`-je: a naptár globális
   * `editable`-je a JOG (van-e `meetings.manage`), ez pedig a TULAJDON (a sajátom-e, vagy van-e
   * `meetings.modify_any`). Enélkül a felület felajánlana egy áthúzást, amit a szerver 403-cal utasít el.
   */
  editable: boolean;
  /**
   * A HALVÁNYÍTÁS jelölő-osztálya: `not-mine` = NEM vagyok a résztvevők között. Osztályként (nem inline
   * stílusként) megy, mert így EGY CSS-szabály fedi mind a négy nézetet (hónap/hét/nap/lista), és a
   * téma-váltás sem tudja elcsúsztatni.
   */
  classNames: string[];
  extendedProps: {
    locationKey: string | null;
    participantCount: number;
    organizerName: string | null;
    /** RÉSZT VESZEK-E rajta (a résztvevők között vagyok-e) — a halványítás és a jelölések forrása. */
    mine: boolean;
    /** Ismétlődő sorozat alkalma-e (az `is-series` jelölés forrása). */
    series: boolean;
  };
}

/**
 * RÉSZT VESZEK-E a megbeszélésen: KIZÁRÓLAG a résztvevő-lista dönt.
 *
 * A SZERVEZŐSÉG SZÁNDÉKOSAN NEM SZÁMÍT (user-döntés): korábban a szervező sora is „sajátnak"
 * (erős színnel) látszott akkor is, ha nem volt a résztvevők között — de az összehívás nem részvétel. A
 * naptár erős/halvány kontrasztja EGY kérdésre válaszol: „ott KELL lennem?". Egy általam összehívott, de
 * nélkülem tartott megbeszélés ugyanúgy halvány, mint bármelyik idegen sor.
 *
 * NB: a MÓDOSÍTHATÓSÁG külön dimenzió — azt a `canModifyMeeting` (tulajdon + `meetings.modify_any`) dönti, és
 * ezen a változáson nem esik: a szervező továbbra is szerkesztheti a sorát, csak nem látszik résztvevőnek.
 */
export function isMyMeeting(row: MeetingRow, currentUserId: string | null): boolean {
  if (currentUserId === null) return false;
  return row.participants.some((p) => p.id === currentUserId);
}

/**
 * Módosíthatom-e EZT a sort? A `meetings.manage` jog a KAPU (azt a hívó adja `canManage`-ként), a tulajdon
 * pedig itt dől el: a sajátom (én hoztam létre), vagy van `meetings.modify_any` jogom. Ugyanaz a szabály, mint
 * a BFF-en (`canModifyMeetingRow`) — a UI csak KÉNYELEM, a fal a szerveren van.
 */
export function canModifyMeeting(
  row: MeetingRow,
  currentUserId: string | null,
  canModifyAny: boolean,
): boolean {
  if (canModifyAny) return true;
  if (currentUserId === null) return false;
  return row.organizerId !== null && row.organizerId === currentUserId;
}

/**
 * Egy `MeetingRow` → naptár-esemény. A `startsAt` HIÁNYA (null) → `null` (kezdet nélkül nem helyezhető el a
 * naptáron → kihagyjuk). Vég-időpont hiányában a kezdettel egyező (nulla hosszú) esemény. A `title` NYERS
 * SZÖVEGKÉNT megy — a FullCalendar escape-eli (nincs HTML-injektálás).
 */
export function meetingToCalendarEvent(
  row: MeetingRow,
  theme: 'light' | 'dark',
  /** A bejelentkezett identity (a „sajátom-e" jelöléshez) — `null` → minden sor idegennek számít. */
  currentUserId: string | null = null,
  /** Van-e `meetings.modify_any` jog (MÁS sorának módosítása) — enélkül csak a sajátom draggelhető. */
  canModifyAny = false,
): MeetingCalendarEvent | null {
  if (!row.startsAt) return null;
  const colors = MEETING_EVENT_COLORS[locationColorKey(row.locationKey)]?.[theme];
  if (!colors) return null;
  const mine = isMyMeeting(row, currentUserId);
  return {
    id: row.id,
    title: row.title || '',
    // A naptár a megjelenítési zóna FAL-IDEJÉT kapja (naiv ISO) — lásd a fájl fejlécét.
    start: utcIsoToCalendarInput(row.startsAt),
    end: utcIsoToCalendarInput(row.endsAt ?? row.startsAt),
    backgroundColor: colors.bg,
    borderColor: colors.border,
    textColor: colors.text,
    editable: canModifyMeeting(row, currentUserId, canModifyAny),
    classNames: [...(mine ? [] : ['not-mine']), ...(row.seriesId ? ['is-series'] : [])],
    extendedProps: {
      locationKey: row.locationKey,
      participantCount: row.participantCount,
      organizerName: row.organizerName,
      mine,
      series: row.seriesId !== null,
    },
  };
}

/** MeetingRow-lista → naptár-események (a kezdet nélküli sorok kiesnek). */
export function rowsToCalendarEvents(
  rows: readonly MeetingRow[],
  theme: 'light' | 'dark',
  currentUserId: string | null = null,
  canModifyAny = false,
): MeetingCalendarEvent[] {
  return rows
    .map((row) => meetingToCalendarEvent(row, theme, currentUserId, canModifyAny))
    .filter((e): e is MeetingCalendarEvent => e !== null);
}

/**
 * A naptár látható tartománya → a BFF `from`/`to` szűrője (UTC ISO). Egy nap puffert adunk mindkét oldalra
 * (a zóna-eltolódás és a nézet-határok fedésére).
 */
export function rangeToIso(range: { start: Date; end: Date }): { from: string; to: string } {
  const DAY_MS = 24 * 60 * 60 * 1000;
  // A naptár `timeZone: 'UTC'`-vel fut, tehát a látható tartomány Date-jei a MEGJELENÍTÉSI zóna fal-idejét
  // hordozzák („fake-UTC") — valódi instantra fordítjuk, mielőtt a BFF szűrőjébe menne. (A ±1 nap puffer
  // amúgy is elfedné a 1–2 órás eltérést, de a szűrő legyen pontos: így a határ-napi meeting sem eshet ki.)
  const fromMs = new Date(calendarDateToUtcIso(range.start)).getTime();
  const toMs = new Date(calendarDateToUtcIso(range.end)).getTime();
  return {
    from: new Date(fromMs - DAY_MS).toISOString(),
    to: new Date(toMs + DAY_MS).toISOString(),
  };
}

/** A részlet-kártya (peek) szélessége pixelben — a `MeetingPeekCard` `w-[21rem]`-je (16 px-es rem mellett). */
export const PEEK_CARD_WIDTH_PX = 336;

/**
 * A részlet-kártya HORGONYA egy kattintott eseményhez. Alapból az esemény téglalapja (a kártya mellette nyílik). HA
 * az esemény SZÉLESEBB, mint maga a kártya (nap-nézet: a teljes szélesség), a horgony a KATTINTÁS vízszintes pontja
 * — különben a kártyának egyik oldalon sem lenne helye, és kicsúszott a képből (mért hiba a nap-nézetben).
 */
export function peekAnchorFor(
  rect: { top: number; left: number; width: number; height: number },
  clickX: number,
): { top: number; left: number; width: number; height: number } {
  if (rect.width <= PEEK_CARD_WIDTH_PX) {
    return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
  }
  return { top: rect.top, left: clickX, width: 0, height: rect.height };
}

/** Egy foglaltsági háttér-sáv (a FullCalendar `display: 'background'` eseménye). */
export interface BusyBackgroundEvent {
  id: string;
  start: string;
  end: string;
  display: 'background';
  classNames: string[];
}

/**
 * FOGLALTSÁGI RÉTEG a kolléga-szűrőhöz: a (már szűrt) megbeszélések idősávjai ÖSSZEVONVA — az átfedő vagy egymáshoz
 * érő sávok egy sávvá olvadnak. A hét/nap nézet ezt halvány, sávozott háttérként rajzolja, így a kiválasztott
 * kollégák KÖZÖS szabad ideje ránézésre kiadódik (a fehéren maradt rész). Az időpontok ugyanúgy a budapesti
 * fal-időre fordulnak, mint az eseményeké. Hiányos (kezdet/vég nélküli) sor kimarad.
 */
export function busyBackgroundEvents(rows: readonly MeetingRow[]): BusyBackgroundEvent[] {
  const spans = rows
    .filter((r): r is MeetingRow & { startsAt: string; endsAt: string } =>
      Boolean(r.startsAt && r.endsAt),
    )
    // időpontként (ms) hasonlítunk — az ISO-SZÖVEG összevetése a formátumon múlna (`…:00Z` vs `…:00.000Z`)
    .map((r) => ({ start: new Date(r.startsAt).getTime(), end: new Date(r.endsAt).getTime() }))
    .filter((s) => Number.isFinite(s.start) && Number.isFinite(s.end) && s.start < s.end)
    .sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [];
  for (const s of spans) {
    const last = merged.at(-1);
    if (last && s.start <= last.end) {
      if (s.end > last.end) last.end = s.end;
    } else {
      merged.push({ ...s });
    }
  }
  return merged.map((m, i) => ({
    id: `busy-${String(i)}`,
    start: utcIsoToCalendarInput(new Date(m.start).toISOString()),
    end: utcIsoToCalendarInput(new Date(m.end).toISOString()),
    display: 'background',
    classNames: ['busy-layer'],
  }));
}
